// src/modules/Admin/pages/MeuManual/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  FaBook,
  FaClock,
  FaDesktop,
  FaChartLine,
  FaFileContract,
  FaLink,
  FaQuestionCircle,
  FaSpinner,
  FaCheck,
  FaCheckCircle,
  FaCircle,
  FaExclamationTriangle
} from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
`;

const ProgressCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ProgressTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111;
`;

const ProgressPercent = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$complete ? '#10b981' : '#666'};
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${props => props.$percent === 100 ? '#10b981' : '#3b82f6'};
  border-radius: 4px;
  transition: width 300ms ease;
`;

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    gap: 8px;
    padding-bottom: 8px;
  }
`;

const SidebarItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 10px;
  background: ${props => props.$active ? '#111' : 'white'};
  color: ${props => props.$active ? 'white' : '#333'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  margin-bottom: 8px;
  transition: all 200ms;
  box-shadow: ${props => props.$active ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.08)'};

  @media (max-width: 768px) {
    width: auto;
    white-space: nowrap;
    flex-shrink: 0;
    margin-bottom: 0;
  }

  &:hover {
    background: ${props => props.$active ? '#111' : '#f5f5f5'};
  }

  svg {
    font-size: 16px;
    flex-shrink: 0;
  }

  .check {
    margin-left: auto;
    color: ${props => props.$active ? '#10b981' : '#10b981'};
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const ContentHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ContentTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #666;
  }
`;

const MarkAsReadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: ${props => props.$read ? '#ecfdf5' : '#f5f5f5'};
  color: ${props => props.$read ? '#059669' : '#666'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: ${props => props.$read ? '#d1fae5' : '#e5e5e5'};
  }
`;

const ContentBody = styled.div`
  padding: 24px;
`;

const MarkdownContent = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: #333;

  h1 {
    font-size: 24px;
    font-weight: 700;
    color: #111;
    margin: 32px 0 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #f0f0f0;

    &:first-child {
      margin-top: 0;
    }
  }

  h2 {
    font-size: 20px;
    font-weight: 600;
    color: #111;
    margin: 24px 0 12px;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 20px 0 10px;
  }

  p {
    margin-bottom: 16px;
  }

  ul, ol {
    margin-bottom: 16px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  strong {
    font-weight: 600;
    color: #111;
  }

  a {
    color: #3b82f6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  code {
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 13px;
    font-family: monospace;
  }

  pre {
    background: #1e1e1e;
    color: #d4d4d4;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;

    code {
      background: none;
      padding: 0;
      color: inherit;
    }
  }

  blockquote {
    border-left: 4px solid #3b82f6;
    padding-left: 16px;
    margin: 16px 0;
    color: #666;
    font-style: italic;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;

    th, td {
      padding: 12px;
      text-align: left;
      border-bottom: 1px solid #e5e5e5;
    }

    th {
      background: #f9fafb;
      font-weight: 600;
      color: #111;
    }
  }

  hr {
    border: none;
    border-top: 1px solid #e5e5e5;
    margin: 24px 0;
  }
`;

const EmptyContent = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;

  svg {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #666;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
  }
`;

const ContractSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 2px solid #f0f0f0;
`;

const ContractCheckbox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: ${props => props.$checked ? '#ecfdf5' : '#f9fafb'};
  border: 2px solid ${props => props.$checked ? '#10b981' : '#e5e5e5'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    border-color: ${props => props.$checked ? '#10b981' : '#ccc'};
  }

  input {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    cursor: pointer;
  }

  .text {
    flex: 1;

    .title {
      font-size: 14px;
      font-weight: 600;
      color: ${props => props.$checked ? '#059669' : '#333'};
      margin-bottom: 4px;
    }

    .description {
      font-size: 13px;
      color: #666;
    }
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;

  svg {
    animation: spin 1s linear infinite;
    margin-right: 10px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const NoJobState = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  svg {
    font-size: 64px;
    color: #ddd;
    margin-bottom: 24px;
  }

  h2 {
    font-size: 20px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    color: #666;
    max-width: 400px;
    margin: 0 auto;
  }
`;

// Configuração das seções
const SECTIONS = [
  { key: 'overview', label: 'Visão Geral', icon: FaBook, progressKey: 'read_overview' },
  { key: 'daily_routine', label: 'Rotina Diária', icon: FaClock, progressKey: 'read_daily_routine' },
  { key: 'platform_guide', label: 'Plataforma', icon: FaDesktop, progressKey: 'read_platform_guide' },
  { key: 'goals_metrics', label: 'Metas', icon: FaChartLine, progressKey: 'read_goals_metrics' },
  { key: 'contract_terms', label: 'Contrato', icon: FaFileContract, progressKey: 'read_contract_terms' },
  { key: 'resources', label: 'Recursos', icon: FaLink, progressKey: 'read_resources' },
  { key: 'faq', label: 'FAQ', icon: FaQuestionCircle, progressKey: 'read_faq' }
];

const MeuManual = () => {
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [script, setScript] = useState(null);
  const [progress, setProgress] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.email) {
      fetchUserJobAndScript();
    }
  }, [user]);

  const fetchUserJobAndScript = async () => {
    try {
      // Buscar dados do usuário com sua vaga
      const { data: userData, error: userError } = await supabase
        .from('admin_users')
        .select('*, jobs(*)')
        .eq('email', user.email)
        .single();

      if (userError) throw userError;

      if (userData?.job_id && userData?.jobs) {
        setJob(userData.jobs);

        // Buscar script da vaga
        const { data: scriptData } = await supabase
          .from('job_scripts')
          .select('*')
          .eq('job_id', userData.job_id)
          .single();

        setScript(scriptData);

        // Buscar progresso do usuário
        const { data: progressData } = await supabase
          .from('user_script_progress')
          .select('*')
          .eq('user_email', user.email)
          .eq('job_id', userData.job_id)
          .single();

        if (progressData) {
          setProgress(progressData);
        } else {
          // Criar registro de progresso
          const { data: newProgress } = await supabase
            .from('user_script_progress')
            .insert({
              user_email: user.email,
              job_id: userData.job_id
            })
            .select()
            .single();

          setProgress(newProgress);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar manual:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (sectionKey) => {
    if (!progress || !job) return;

    const progressKey = SECTIONS.find(s => s.key === sectionKey)?.progressKey;
    if (!progressKey) return;

    const newValue = !progress[progressKey];

    try {
      const { error } = await supabase
        .from('user_script_progress')
        .update({
          [progressKey]: newValue,
          last_access: new Date().toISOString()
        })
        .eq('id', progress.id);

      if (error) throw error;

      setProgress(prev => ({ ...prev, [progressKey]: newValue }));
    } catch (error) {
      console.error('Erro ao atualizar progresso:', error);
    }
  };

  const acceptContract = async () => {
    if (!progress) return;

    try {
      const { error } = await supabase
        .from('user_script_progress')
        .update({
          contract_accepted: true,
          contract_accepted_at: new Date().toISOString(),
          read_contract_terms: true
        })
        .eq('id', progress.id);

      if (error) throw error;

      setProgress(prev => ({
        ...prev,
        contract_accepted: true,
        contract_accepted_at: new Date().toISOString(),
        read_contract_terms: true
      }));
    } catch (error) {
      console.error('Erro ao aceitar contrato:', error);
    }
  };

  // Calcular progresso
  const calculateProgress = () => {
    if (!progress) return 0;
    const total = SECTIONS.length;
    const read = SECTIONS.filter(s => progress[s.progressKey]).length;
    return Math.round((read / total) * 100);
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FaSpinner /> Carregando seu manual...
        </LoadingState>
      </Container>
    );
  }

  if (!job) {
    return (
      <Container>
        <NoJobState>
          <FaExclamationTriangle />
          <h2>Nenhuma vaga atribuída</h2>
          <p>
            Você ainda não foi vinculado a uma vaga específica.
            Entre em contato com o administrador para ser atribuído a uma função.
          </p>
        </NoJobState>
      </Container>
    );
  }

  const currentSection = SECTIONS.find(s => s.key === activeSection);
  const currentContent = script?.[activeSection] || '';
  const isRead = progress?.[currentSection?.progressKey] || false;
  const progressPercent = calculateProgress();

  return (
    <Container>
      <Header>
        <Title>Meu Manual</Title>
        <Subtitle>Manual do Colaborador - {job.title}</Subtitle>
      </Header>

      <ProgressCard>
        <ProgressHeader>
          <ProgressTitle>Seu progresso de leitura</ProgressTitle>
          <ProgressPercent $complete={progressPercent === 100}>
            {progressPercent}% completo
          </ProgressPercent>
        </ProgressHeader>
        <ProgressBar>
          <ProgressFill $percent={progressPercent} />
        </ProgressBar>
      </ProgressCard>

      <SectionsGrid>
        <Sidebar>
          {SECTIONS.map(section => {
            const Icon = section.icon;
            const sectionRead = progress?.[section.progressKey] || false;
            return (
              <SidebarItem
                key={section.key}
                $active={activeSection === section.key}
                onClick={() => setActiveSection(section.key)}
              >
                <Icon />
                {section.label}
                {sectionRead && (
                  <FaCheckCircle className="check" />
                )}
              </SidebarItem>
            );
          })}
        </Sidebar>

        <ContentCard>
          <ContentHeader>
            <ContentTitle>
              <currentSection.icon />
              {currentSection.label}
            </ContentTitle>
            <MarkAsReadButton
              $read={isRead}
              onClick={() => markAsRead(activeSection)}
            >
              {isRead ? <FaCheckCircle /> : <FaCircle />}
              {isRead ? 'Lido' : 'Marcar como lido'}
            </MarkAsReadButton>
          </ContentHeader>

          <ContentBody>
            {currentContent ? (
              <>
                <MarkdownContent>
                  <ReactMarkdown>{currentContent}</ReactMarkdown>
                </MarkdownContent>

                {activeSection === 'contract_terms' && !progress?.contract_accepted && (
                  <ContractSection>
                    <ContractCheckbox
                      $checked={progress?.contract_accepted}
                      onClick={acceptContract}
                    >
                      <input
                        type="checkbox"
                        checked={progress?.contract_accepted || false}
                        onChange={() => {}}
                      />
                      <div className="text">
                        <div className="title">
                          {progress?.contract_accepted
                            ? 'Contrato aceito'
                            : 'Li e aceito os termos do contrato'}
                        </div>
                        <div className="description">
                          Ao marcar esta opção, você confirma que leu e concorda com todos os termos descritos acima.
                        </div>
                      </div>
                    </ContractCheckbox>
                  </ContractSection>
                )}

                {activeSection === 'contract_terms' && progress?.contract_accepted && (
                  <ContractSection>
                    <ContractCheckbox $checked={true}>
                      <FaCheckCircle style={{ color: '#10b981', fontSize: 20 }} />
                      <div className="text">
                        <div className="title">Contrato aceito</div>
                        <div className="description">
                          Você aceitou os termos em {new Date(progress.contract_accepted_at).toLocaleDateString('pt-BR')}
                        </div>
                      </div>
                    </ContractCheckbox>
                  </ContractSection>
                )}
              </>
            ) : (
              <EmptyContent>
                <currentSection.icon />
                <h3>Conteúdo não disponível</h3>
                <p>Esta seção ainda não foi preenchida pelo administrador.</p>
              </EmptyContent>
            )}
          </ContentBody>
        </ContentCard>
      </SectionsGrid>
    </Container>
  );
};

export default MeuManual;
